import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncData } from '@/hooks';
import { institutionExperienceApi } from '@/api/institution-experience';
import type {
  CapabilityPackDefinition,
  ResolvedCapabilitySet,
  ResolvedPack,
} from '@/types/institution-experience';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { computePackDiff, isDeparture, type PackDiff } from './pack-diff';
import styles from '../AdminExperiencePage.module.css';

const NS = 'admin.experience.packs';

/**
 * The capability pack grid: what this institution's screens are allowed to show,
 * pack by pack, each explaining WHY it is on or off.
 *
 * The source sentence is written in words rather than left as a badge code because
 * the four sources answer four different questions an auditor asks: "on by default"
 * is nobody's decision, "added" and "removed" are THIS school's decisions against
 * its type, and "confirmed" is a decision that happens to agree. The subtle
 * departure badge exists so the two decisions that changed anything can be found by
 * scanning, without reading every card.
 *
 * Toggling goes through a confirm dialog that names the true capability/module
 * delta — computed against the union of the other active packs, not the pack's own
 * contents — and requires a reason, which the server stores as the note the next
 * administrator reads on this same card.
 */
export function CapabilityPacksTab({ institutionId }: { institutionId: string }) {
  const { t } = useTranslation();
  const toast = useToast();

  const { state: catalogueState, reload: reloadCatalogue } = useAsyncData<CapabilityPackDefinition[]>(
    'experience-pack-catalogue',
    () => institutionExperienceApi.capabilityPacks.catalogue(),
    true,
  );

  const {
    state: resolvedState,
    reload: reloadResolved,
    replace: replaceResolved,
  } = useAsyncData<ResolvedCapabilitySet>(
    `experience-packs-${institutionId}`,
    () => institutionExperienceApi.capabilityPacks.resolved(institutionId),
    !!institutionId,
  );

  const catalogue = catalogueState.status === 'ready' ? catalogueState.data : [];
  const byPack = useMemo(
    () => new Map(catalogue.map((definition) => [definition.pack, definition])),
    [catalogue],
  );

  const [toggling, setToggling] = useState<{ pack: ResolvedPack; enable: boolean } | null>(null);
  const [clearing, setClearing] = useState<ResolvedPack | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const confirmToggle = async (reason: string) => {
    if (!toggling) return;
    setSubmitting(true);
    setDialogError(null);
    try {
      const next = await institutionExperienceApi.capabilityPacks.set(institutionId, toggling.pack.pack, {
        enabled: toggling.enable,
        note: reason,
      });
      // The PUT answers with the recomputed set; replacing state with it keeps the
      // whole grid consistent without a refetch.
      replaceResolved(next);
      toast.success(
        t(toggling.enable ? `${NS}.enabled` : `${NS}.disabled`, { pack: toggling.pack.title }),
      );
      setToggling(null);
    } catch (cause) {
      // The 403/409 sentence is the server's; it is shown as written.
      setDialogError(cause instanceof Error ? cause.message : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmClear = async () => {
    if (!clearing) return;
    setSubmitting(true);
    setDialogError(null);
    try {
      const next = await institutionExperienceApi.capabilityPacks.clear(institutionId, clearing.pack);
      replaceResolved(next);
      toast.success(t(`${NS}.cleared`, { pack: clearing.title }));
      setClearing(null);
    } catch (cause) {
      setDialogError(cause instanceof Error ? cause.message : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.section}>
      <p className={styles.toolbarLead}>{t(`${NS}.lead`)}</p>

      <AsyncPanel state={resolvedState} onRetry={reloadResolved}>
        {(resolved) => (
          <>
            {/* Server warnings, verbatim and before anything else: "no configuration
                yet, so no defaults apply" changes how every card should be read. */}
            {resolved.warnings.length > 0 && (
              <div className={styles.warnings}>
                {resolved.warnings.map((warning) => (
                  <Alert key={warning} tone="warning" title={t(`${NS}.warningTitle`)}>
                    {warning}
                  </Alert>
                ))}
              </div>
            )}

            <AsyncPanel
              state={catalogueState}
              onRetry={reloadCatalogue}
              isEmpty={(data) => data.length === 0}
              emptyTitle={t(`${NS}.catalogueEmpty`)}
              emptyBody={t(`${NS}.catalogueEmptyBody`)}
            >
              {() => (
                <ul className={styles.packGrid}>
                  {resolved.packDetail.map((pack) => (
                    <PackCard
                      key={pack.pack}
                      pack={pack}
                      definition={byPack.get(pack.pack)}
                      onToggle={(enable) => {
                        setDialogError(null);
                        setToggling({ pack, enable });
                      }}
                      onClear={() => {
                        setDialogError(null);
                        setClearing(pack);
                      }}
                    />
                  ))}
                </ul>
              )}
            </AsyncPanel>

            {toggling && (
              <ToggleDialog
                key={`${toggling.pack.pack}-${toggling.enable}`}
                pack={toggling.pack}
                enable={toggling.enable}
                diff={computePackDiff(catalogue, resolved.packDetail, toggling.pack.pack, toggling.enable)}
                definitionKnown={byPack.has(toggling.pack.pack)}
                submitting={submitting}
                error={dialogError}
                onCancel={() => setToggling(null)}
                onConfirm={confirmToggle}
              />
            )}

            {clearing && (
              <ClearDialog
                pack={clearing}
                diff={
                  // Clearing an ADDED pack turns it off; clearing a REMOVED pack turns
                  // it back on; clearing CONFIRMED changes nothing. The diff shows the
                  // state change the default would reimpose.
                  clearing.source === 'CONFIRMED'
                    ? null
                    : computePackDiff(
                        catalogue,
                        resolved.packDetail,
                        clearing.pack,
                        clearing.source === 'REMOVED',
                      )
                }
                submitting={submitting}
                error={dialogError}
                onCancel={() => setClearing(null)}
                onConfirm={confirmClear}
              />
            )}
          </>
        )}
      </AsyncPanel>
    </section>
  );
}

function PackCard({
  pack,
  definition,
  onToggle,
  onClear,
}: {
  pack: ResolvedPack;
  definition: CapabilityPackDefinition | undefined;
  onToggle: (enable: boolean) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li>
      <Card className={styles.packCard}>
        <div className={styles.packHead}>
          <h3 className={styles.packTitle}>{pack.title}</h3>
          <Badge tone={pack.active ? 'success' : 'neutral'}>
            {t(pack.active ? `${NS}.active` : `${NS}.inactive`)}
          </Badge>
          {isDeparture(pack.source) && (
            <Badge tone="warning" title={t(`${NS}.departureHint`)}>
              {t(`${NS}.departure`)}
            </Badge>
          )}
        </div>

        {definition?.summary && <p className={styles.packSummary}>{definition.summary}</p>}

        {/* The provenance sentence — the audit answer, in words. */}
        <p className={styles.packSource}>{t(`${NS}.sourceText.${pack.source}`)}</p>

        {pack.note && (
          <p className={styles.packNote}>
            {t(`${NS}.noteLabel`)} <q>{pack.note}</q>
          </p>
        )}

        {definition && definition.modules.length > 0 && (
          <ul className={styles.chipRow} aria-label={t(`${NS}.modulesLabel`)}>
            {definition.modules.map((module) => (
              <li key={module} className={styles.chip}>
                {module}
              </li>
            ))}
          </ul>
        )}

        <div className={styles.packActions}>
          <Button
            variant={pack.active ? 'secondary' : 'primary'}
            size="sm"
            onClick={() => onToggle(!pack.active)}
          >
            {t(pack.active ? `${NS}.disable` : `${NS}.enable`)}
          </Button>
          {pack.source !== 'DEFAULT' && (
            <Button variant="ghost" size="sm" onClick={onClear}>
              {t(`${NS}.clear`)}
            </Button>
          )}
        </div>
      </Card>
    </li>
  );
}

/** The diff, rendered as the two lists that matter, with an explicit "nothing changes". */
function DiffSummary({ diff }: { diff: PackDiff }) {
  const { t } = useTranslation();
  if (diff.isNoop) return <p className={styles.diffNoop}>{t(`${NS}.diffNoop`)}</p>;
  return (
    <div className={styles.diff}>
      {diff.modulesAdded.length > 0 && (
        <DiffList label={t(`${NS}.modulesAdded`)} entries={diff.modulesAdded} tone="add" />
      )}
      {diff.modulesRemoved.length > 0 && (
        <DiffList label={t(`${NS}.modulesRemoved`)} entries={diff.modulesRemoved} tone="remove" />
      )}
      {diff.capabilitiesAdded.length > 0 && (
        <DiffList label={t(`${NS}.capabilitiesAdded`)} entries={diff.capabilitiesAdded} tone="add" />
      )}
      {diff.capabilitiesRemoved.length > 0 && (
        <DiffList
          label={t(`${NS}.capabilitiesRemoved`)}
          entries={diff.capabilitiesRemoved}
          tone="remove"
        />
      )}
    </div>
  );
}

function DiffList({
  label,
  entries,
  tone,
}: {
  label: string;
  entries: string[];
  tone: 'add' | 'remove';
}) {
  return (
    <div>
      <p className={styles.diffLabel}>{label}</p>
      <ul className={styles.chipRow}>
        {entries.map((entry) => (
          <li key={entry} className={tone === 'add' ? `${styles.chip} ${styles.chipAdd}` : `${styles.chip} ${styles.chipRemove}`}>
            <code>{entry}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToggleDialog({
  pack,
  enable,
  diff,
  definitionKnown,
  submitting,
  error,
  onCancel,
  onConfirm,
}: {
  pack: ResolvedPack;
  enable: boolean;
  diff: PackDiff;
  definitionKnown: boolean;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmActionDialog
      open
      title={t(enable ? `${NS}.enableTitle` : `${NS}.disableTitle`, { pack: pack.title })}
      intro={t(enable ? `${NS}.enableIntro` : `${NS}.disableIntro`, { pack: pack.title })}
      // Disabling takes screens away from people mid-term; enabling only adds.
      tone={enable ? 'neutral' : 'warning'}
      impactStatus={definitionKnown ? 'ready' : 'unavailable'}
      impact={<DiffSummary diff={diff} />}
      confirmLabel={t(enable ? `${NS}.enable` : `${NS}.disable`)}
      submitting={submitting}
      error={error}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

/**
 * Clearing has no note field server-side (the DELETE takes no body), so this is a
 * plain modal rather than the reasoned dialog — there is nowhere to put a reason.
 */
function ClearDialog({
  pack,
  diff,
  submitting,
  error,
  onCancel,
  onConfirm,
}: {
  pack: ResolvedPack;
  diff: PackDiff | null;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onCancel}
      title={t(`${NS}.clearTitle`, { pack: pack.title })}
      description={t(`${NS}.clearIntro`)}
    >
      <div className={styles.dialogBody}>
        {diff ? <DiffSummary diff={diff} /> : <p className={styles.diffNoop}>{t(`${NS}.clearNoChange`)}</p>}
        {error && (
          <p className={styles.dialogError} role="alert">
            {error}
          </p>
        )}
        <div className="modal-actions">
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm} loading={submitting} loadingLabel={t('common.submitting')}>
            {t(`${NS}.clear`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
